import React from 'react';

import CalendarMonthTwoToneIcon from '@mui/icons-material/CalendarMonthTwoTone';
import CampaignTwoToneIcon from '@mui/icons-material/CampaignTwoTone';
import MessageTwoToneIcon from '@mui/icons-material/MessageTwoTone';
import MonetizationOnTwoToneIcon from '@mui/icons-material/MonetizationOnTwoTone';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

const FEATURES_DATA = [
  {
    id: 'atencion-de-leads',
    icon: <MessageTwoToneIcon fontSize="large" />,
    title: 'Atención de Leads',
    color: '#4f81bd',
    text: 'Sin importar la fuente de tus leads, atiéndelos en el instante, 24/7 con igual o mas información que un vendedor.',
  },
  {
    id: 'financiamiento',
    icon: <MonetizationOnTwoToneIcon fontSize="large" />,
    title: 'Financiamiento',
    color: '#4f81bd',
    text: 'Nos conectamos a las financieras para aprobar créditos directamente en la conversación, aumentando las visitas y probabilidad de compra.',
  },
  {
    id: 'agendamiento',
    icon: <CalendarMonthTwoToneIcon fontSize="large" />,
    title: 'Agendamiento',
    color: '#4f81bd',
    text: 'Guiamos la conversación a una visita efectiva a tus locales, test drive, o mantención.',
  },
  {
    id: 'campanas-outbound',
    icon: <CampaignTwoToneIcon fontSize="large" />,
    title: 'Campañas Outbound',
    color: '#4f81bd',
    text: 'Ocupa tus bases de datos ocupando nuestra herramienta de outbound. Carga campañas sin fricciones y anda a buscar a esos clientes por whatsapp aprovechando tasas de apertura de 98%.',
  },
];

const CENTRAL_TEXT = {
  heading: 'Nos integramos a tu flujo de trabajo',
  subheading:
    'Nos integramos 100% a tus sistemas para no afectar la operación normal de tu concesionario ni agregar sistemas extras a tus vendedores.',
  imageSrc: '/assets/images/chat-example.png',
};

const FeatureCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#4f81bd',
  color: '#FFFFFF',
  height: '100%',
  minHeight: '180px',
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(3),
  borderRadius: '20px',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
  borderRadius: '50%',
  padding: theme.spacing(1.5),
  width: '50px',
  height: '50px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginRight: theme.spacing(2),
}));

const FeatureTitle = styled(Typography)({
  fontWeight: 700,
  letterSpacing: '0.5px',
  marginBottom: '12px',
  fontSize: '1.1rem',
  display: 'flex',
  alignItems: 'center',
});

const FeatureText = styled(Typography)({
  fontSize: '1.05rem',
  lineHeight: '1.5',
  letterSpacing: '0.2px',
  fontWeight: 400,
  textAlign: 'justify',
});

const CentralContent = styled(Box)(({ theme }) => ({
  backgroundColor: '#f5f5f5',
  padding: theme.spacing(5, 4),
  borderRadius: '20px',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  minHeight: '400px',
}));

const MainHeading = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '2rem',
  marginBottom: theme.spacing(1),
  color: '#333',
}));

const SubHeading = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  marginBottom: theme.spacing(4),
  maxWidth: '80%',
  margin: '0 auto',
  color: '#555',
}));

const CRMImageContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  width: '100%',
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    display: 'block',
    margin: '0 auto',
    [theme.breakpoints.down('sm')]: {
      width: '80%',
    },
  },
}));

const FeaturesZeller: React.FC = () => {
  return (
    <Box component="section" sx={{ py: 10, px: 2, backgroundColor: '#fff' }}>
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          <Grid item xs={12} md={3}>
            <Grid container direction="column" spacing={3}>
              {FEATURES_DATA.slice(0, 2).map((feature) => (
                <Grid item key={feature.id}>
                  <FeatureCard>
                    <Box sx={{ display: 'flex', marginBottom: 2 }}>
                      <IconWrapper>{feature.icon}</IconWrapper>
                      <FeatureTitle variant="h6">{feature.title}</FeatureTitle>
                    </Box>
                    <FeatureText variant="body2">{feature.text}</FeatureText>
                  </FeatureCard>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid item xs={12} md={6}>
            <CentralContent>
              <MainHeading variant="h3">{CENTRAL_TEXT.heading}</MainHeading>
              <SubHeading variant="subtitle1">{CENTRAL_TEXT.subheading}</SubHeading>
              <CRMImageContainer>
                <img src="/landing/crm/crms.svg" alt="Salesforce Logo" />
              </CRMImageContainer>
            </CentralContent>
          </Grid>

          <Grid item xs={12} md={3}>
            <Grid container direction="column" spacing={3}>
              {FEATURES_DATA.slice(2, 4).map((feature) => (
                <Grid item key={feature.id}>
                  <FeatureCard>
                    <Box sx={{ display: 'flex', marginBottom: 2 }}>
                      <IconWrapper>{feature.icon}</IconWrapper>
                      <FeatureTitle variant="h6">{feature.title}</FeatureTitle>
                    </Box>
                    <FeatureText variant="body2">{feature.text}</FeatureText>
                  </FeatureCard>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default FeaturesZeller;
